import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/useAuth";
import { deletionReadToken } from "@/auth/deletionAuthentication";
import {
  ACTIVE_DELETION_STATES,
  dismissDeletionIntake,
  finalizeDeletion,
  getDeletionActions,
  getDeletionCase,
  getDeletionIntakes,
  getDeletionRetention,
  getDeletionReviews,
  reviewDeletion,
  verifyDeletionIntake,
} from "@/api/accountDeletion";
import type { DeletionRequest } from "@/api/accountDeletion";
import { useFirestoreDoc, usePaginatedQuery } from "@/firestore/hooks";
import type { Filter } from "@/firestore/hooks";
import { COLLECTIONS } from "@/firestore/collections";
import { accountDeletionPath } from "@/routes/paths";
import { env } from "@/config/env";
import { AccountDeletionQueueView } from "./account-deletion/AccountDeletionQueueView";
import { AccountDeletionDetailView } from "./account-deletion/AccountDeletionDetailView";
import type { DeletionStateFilter, DeletionTab } from "./account-deletion/types";
import { useDeletionCommand, useDeletionPages } from "./useAccountDeletion";

export function AccountDeletionPage() {
  const { user } = useAuth();
  return <DeletionQueues key={user?.uid ?? "signed-out"} />;
}

function DeletionQueues() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab: DeletionTab =
    params.get("tab") === "intakes"
      ? "intakes"
      : params.get("tab") === "retention"
        ? "retention"
        : "requests";
  const [stateFilter, setStateFilter] = useState<DeletionStateFilter>("active");
  const [before, setBefore] = useState(() => new Date(Date.now() + 30 * 86_400_000).toISOString());
  const command = useDeletionCommand();
  const filters: Filter[] =
    stateFilter === "all"
      ? []
      : stateFilter === "active"
        ? [["state", "in", ACTIVE_DELETION_STATES]]
        : [["state", "==", stateFilter]];
  const requests = usePaginatedQuery<DeletionRequest>(
    COLLECTIONS.accountDeletionRequests,
    { filters, orderBy: [["requestedAt", "asc"]] },
    50,
    tab === "requests",
  );
  const intakes = useDeletionPages(
    ["intakes"],
    (token, cursor, signal) => getDeletionIntakes(token, { cursor, signal, pageSize: 50 }),
    tab === "intakes",
  );
  const retention = useDeletionPages(
    ["retention", before],
    (token, cursor, signal) =>
      getDeletionRetention(token, before, { cursor, signal, pageSize: 50 }),
    tab === "retention",
  );

  function refresh() {
    if (tab === "intakes") void intakes.refresh();
    else if (tab === "retention") void retention.refresh();
    else requests.refresh();
  }

  return (
    <AccountDeletionQueueView
      tab={tab}
      onTabChange={(next) => setParams(next === "requests" ? {} : { tab: next })}
      requests={{
        items: requests.data,
        loading: requests.loading,
        error: requests.error,
        hasMore: requests.hasMore,
        onLoadMore: requests.loadMore,
      }}
      stateFilter={stateFilter}
      onStateFilterChange={setStateFilter}
      onLookup={(id) => navigate(accountDeletionPath(id))}
      intakes={intakes}
      retention={retention}
      retentionBefore={before}
      onRetentionBeforeChange={setBefore}
      canAct={command.canAct}
      actionPending={command.pending}
      onVerifyIntake={async (intake, input) => {
        const result = await command.execute(
          `intake:${intake.id}:verify`,
          intake.updatedAt,
          input,
          (token, clientRequestId) =>
            verifyDeletionIntake(token, intake.id, intake.updatedAt, { ...input, clientRequestId }),
        );
        navigate(accountDeletionPath(result.id));
      }}
      onDismissIntake={async (intake, input) => {
        await command.execute(
          `intake:${intake.id}:dismiss`,
          intake.updatedAt,
          input,
          (token, clientRequestId) =>
            dismissDeletionIntake(token, intake.id, intake.updatedAt, {
              ...input,
              clientRequestId,
            }),
        );
        await intakes.refresh();
      }}
      onRefresh={refresh}
    />
  );
}

export function AccountDeletionDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { user } = useAuth();
  return <DeletionCasePage key={`${user?.uid ?? "signed-out"}:${id}`} id={id} />;
}

function DeletionCasePage({ id }: { id: string }) {
  const { user } = useAuth();
  const uid = user?.uid ?? "";
  const command = useDeletionCommand();
  const live = useFirestoreDoc<DeletionRequest>(COLLECTIONS.accountDeletionRequests, id);
  const revision = live.data?.updatedAt ?? "pending-projection";
  const detail = useQuery({
    queryKey: ["account-deletion", env.firebase.projectId, uid, "case", id, revision],
    queryFn: async ({ signal }) => getDeletionCase(await deletionReadToken(uid), id, signal),
    enabled: !!uid && !!id,
    retry: false,
    gcTime: 0,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const evidence = useDeletionPages(["case", id, revision, "reviews"], (token, cursor, signal) =>
    getDeletionReviews(token, id, { cursor, signal, pageSize: 50 }),
  );
  const actions = useDeletionPages(["case", id, revision, "actions"], (token, cursor, signal) =>
    getDeletionActions(token, id, { cursor, signal, pageSize: 50 }),
  );

  async function refresh() {
    if (live.error) live.refresh();
    await Promise.all([detail.refetch(), evidence.refresh(), actions.refresh()]);
  }

  return (
    <AccountDeletionDetailView
      data={detail.data ?? null}
      loading={detail.isPending}
      error={detail.error ?? live.error}
      stale={
        detail.isFetching ||
        !!detail.error ||
        live.loading ||
        !!live.error ||
        (!!live.data &&
          !!detail.data &&
          Date.parse(live.data.updatedAt) > Date.parse(detail.data.request.updatedAt))
      }
      canReview={command.canAct}
      canStartErasure={command.canAct}
      actionPending={command.pending}
      evidence={evidence}
      actions={actions}
      onReview={async (input, updatedAt) => {
        await command.execute(`request:${id}:review`, updatedAt, input, (token, clientRequestId) =>
          reviewDeletion(token, id, updatedAt, { ...input, clientRequestId }),
        );
        await refresh();
      }}
      onFinalize={async (input, updatedAt) => {
        await command.execute(
          `request:${id}:finalize`,
          updatedAt,
          input,
          (token, clientRequestId) =>
            finalizeDeletion(token, id, updatedAt, { ...input, clientRequestId }),
        );
        await refresh();
      }}
      onRefresh={() => {
        void refresh();
      }}
    />
  );
}
